'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface BreadcrumbItemData {
  slug: string;
  name: string;
  href: string;
}

interface ProductBreadcrumbProps {
  items: BreadcrumbItemData[];
  productName: string;
}

export default function ProductBreadcrumb({ items, productName }: ProductBreadcrumbProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const [visibleCount, setVisibleCount] = useState<number>(items.length);

  useEffect(() => {
    const updateVisibility = () => {
      if (!containerRef.current || !listRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const listItems = Array.from(listRef.current.children) as HTMLElement[];
      
      if (listItems.length === 0) return;

      // Все элементы рендерятся, теперь проверяем видимость
      let totalWidth = 0;
      let visibleCount = 0;
      const ellipsisWidth = 60; // примерная ширина ellipsis + dropdown
      const productNameWidth = 200; // запас для названия товара
      let hasHiddenItems = false;

      // Проходим по элементам категорий
      // В списке каждый элемент категории идет с разделителем (кроме последнего)
      for (let i = 0; i < items.length; i++) {
        // Индекс элемента категории в DOM (учитывая разделители)
        const itemIndex = i * 2;
        const separatorIndex = itemIndex + 1;

        if (itemIndex >= listItems.length) break;

        const itemElement = listItems[itemIndex];
        const separatorElement = separatorIndex < listItems.length ? listItems[separatorIndex] : null;

        if (!itemElement) continue;

        // Получаем реальную ширину элемента
        const itemWidth = itemElement.getBoundingClientRect().width;
        const separatorWidth = separatorElement ? separatorElement.getBoundingClientRect().width : 0;
        const neededWidth = totalWidth + itemWidth + separatorWidth;

        // Проверяем, помещается ли элемент
        const availableWidth = containerWidth - (hasHiddenItems ? ellipsisWidth : 0) - productNameWidth;

        if (neededWidth <= availableWidth) {
          visibleCount++;
          totalWidth += itemWidth + separatorWidth;
        } else {
          hasHiddenItems = true;
          break;
        }
      }

      setVisibleCount(Math.max(visibleCount, 0));
    };

    // Проверяем видимость после рендера
    const timeoutId = setTimeout(updateVisibility, 150);

    // Слушаем изменения размера
    const resizeObserver = new ResizeObserver(() => {
      updateVisibility();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', updateVisibility);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateVisibility);
    };
  }, [items]);

  const visibleItems = items.slice(0, visibleCount);
  const hiddenItems = items.slice(visibleCount);
  const hasHiddenItems = hiddenItems.length > 0;

  return (
    <div
      ref={containerRef}
      className="w-full overflow-x-auto"
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <style jsx global>{`
        .product-breadcrumb-scroll::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <div className="product-breadcrumb-scroll">
        <Breadcrumb>
          <BreadcrumbList ref={listRef} className="flex-nowrap whitespace-nowrap">
            {visibleItems.map((item, index) => (
              <React.Fragment key={item.slug}>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href={item.href}>{item.name}</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {(index < visibleItems.length - 1 || hasHiddenItems) && <BreadcrumbSeparator />}
              </React.Fragment>
            ))}
            
            {hasHiddenItems && (
              <>
                <BreadcrumbItem>
                  <DropdownMenu>
                    <DropdownMenuTrigger className="flex items-center gap-1">
                      <BreadcrumbEllipsis className="h-4 w-4" />
                      <span className="sr-only">Toggle menu</span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {hiddenItems.map((item) => (
                        <DropdownMenuItem key={item.slug}>
                          <Link href={item.href} className="w-full">
                            {item.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </BreadcrumbItem>
              </>
            )}
            
            {(visibleItems.length > 0 || hasHiddenItems) && <BreadcrumbSeparator />}
            
            <BreadcrumbItem>
              <BreadcrumbPage>{productName}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </div>
  );
}

